/**
 * service-kyc client. Mirrors:
 *   apps/service-kyc/src/modules/verification/verification.controller.ts
 *   apps/service-kyc/src/modules/kyc-profile/kyc-profile.controller.ts
 *   apps/service-kyc/src/modules/sar/sar.controller.ts
 *   apps/service-kyc/src/modules/watchlist/watchlist.controller.ts
 *
 * Phase 2b surface:
 *   - POST  /v1/kyc/verifications/nin       — start a NIN verification
 *   - GET   /v1/kyc/verifications/:id       — fetch one
 *   - POST  /v1/kyc/profiles/:customerId/get-or-create — ensure a profile exists
 *
 * Phase 3b extensions:
 *   - POST  /v1/kyc/verifications/bvn       — BVN verification (NIBSS stub)
 *   - POST  /v1/kyc/verifications/biometric — biometric verification
 *   - GET   /v1/kyc/sars                    — paginated list of SARs
 *   - GET   /v1/kyc/watchlist/queue         — review queue
 *   - POST  /v1/kyc/watchlist/hits/:id/review — review a watchlist hit
 *
 * RBAC: NIN verification requires `Kyc/verify`; SAR / watchlist operations
 * require `Kyc/sar-create`, `Kyc/sar-submit`, `Kyc/screen`, `Kyc/review`.
 */
import { env } from '../env';

import { request } from './client';

const baseUrl = (): string => env.serviceKycBaseUrl;

export type VerificationStatus =
  | 'PENDING'
  | 'IN_PROGRESS'
  | 'VERIFIED'
  | 'FAILED'
  | 'EXPIRED';

export interface VerificationResponse {
  readonly id: string;
  readonly type: 'NIN' | 'BVN' | 'BIOMETRIC';
  readonly status: VerificationStatus;
  readonly subjectId: string;
  readonly providerRef?: string;
  readonly evidence?: Record<string, unknown>;
  readonly createdAt: string;
  readonly updatedAt: string;
}

export interface NinVerifyRequest {
  readonly nin: string;
  readonly customerId?: string;
  readonly fullNameHint?: string;
}

export async function verifyNin(dto: NinVerifyRequest): Promise<VerificationResponse> {
  return request<VerificationResponse>(baseUrl(), '/v1/kyc/verifications/nin', {
    method: 'POST',
    body: dto,
  });
}

export async function getVerification(id: string): Promise<VerificationResponse> {
  return request<VerificationResponse>(
    baseUrl(),
    `/v1/kyc/verifications/${encodeURIComponent(id)}`,
    { method: 'GET' },
  );
}

// ─── BVN verification ─────────────────────────────────────────────────────

export interface BvnVerifyRequest {
  readonly profileId: string;
  readonly bvn: string;
}

export async function verifyBvn(dto: BvnVerifyRequest): Promise<VerificationResponse> {
  return request<VerificationResponse>(baseUrl(), '/v1/kyc/verifications/bvn', {
    method: 'POST',
    body: dto,
  });
}

// ─── Biometric verification ───────────────────────────────────────────────

export interface BiometricVerifyRequest {
  readonly profileId: string;
  readonly faceImageRef?: string;
  readonly fingerprintRef?: string;
}

export async function verifyBiometric(
  dto: BiometricVerifyRequest,
): Promise<{ readonly items: readonly VerificationResponse[] }> {
  return request<{ readonly items: readonly VerificationResponse[] }>(
    baseUrl(),
    '/v1/kyc/verifications/biometric',
    { method: 'POST', body: dto },
  );
}

// ─── SARs ─────────────────────────────────────────────────────────────────

export type SarStatus =
  | 'DRAFT'
  | 'SUBMITTED'
  | 'ACKNOWLEDGED'
  | 'REJECTED';

export interface SarSummary {
  readonly id: string;
  readonly tenantId: string;
  readonly profileId: string;
  readonly sarNumber: string;
  readonly trigger: string;
  readonly status: SarStatus;
  readonly totalAmount: string;
  readonly currency: string;
  readonly windowStart: string;
  readonly windowEnd: string;
  readonly narrative: string;
  readonly goAmlRef: string | null;
  readonly filedBy: string | null;
  readonly filedAt: string | null;
  readonly acknowledgedAt: string | null;
  readonly createdBy: string;
  readonly createdAt: string;
}

export interface ListSarsResponse {
  readonly items: readonly SarSummary[];
  // eslint-disable-next-line upmp/no-number-money -- row count, not money
  readonly total: number;
  readonly page: number;
  readonly pageSize: number;
}

export async function listSars(query: {
  readonly status?: SarStatus;
  readonly page?: number;
  readonly pageSize?: number;
} = {}): Promise<ListSarsResponse> {
  return request<ListSarsResponse>(baseUrl(), '/v1/kyc/sars', {
    method: 'GET',
    query: { status: query.status, page: query.page, pageSize: query.pageSize },
  });
}

// ─── Watchlist ────────────────────────────────────────────────────────────

export type WatchlistHitStatus =
  | 'ACTIVE'
  | 'CONFIRMED'
  | 'FALSE_POSITIVE'
  | 'CLEARED';

export type WatchlistSource = 'NFIU_PEP' | 'OFAC_SDN' | 'CBN_SANCTIONS' | 'INTERNAL_BLOCKLIST';

export interface WatchlistHit {
  readonly id: string;
  readonly profileId: string;
  readonly listSource: WatchlistSource;
  readonly matchKind: string;
  readonly confidence: number;
  readonly status: WatchlistHitStatus;
  readonly reviewedBy: string | null;
  readonly reviewedAt: string | null;
  readonly reviewNote: string | null;
  readonly detectedAt: string;
  readonly details: Record<string, unknown> | null;
}

export interface ListWatchlistHitsResponse {
  readonly items: readonly WatchlistHit[];
  // eslint-disable-next-line upmp/no-number-money -- row count, not money
  readonly total: number;
  readonly page: number;
  readonly pageSize: number;
}

export async function listWatchlistHits(query: {
  readonly status?: WatchlistHitStatus;
  readonly page?: number;
  readonly pageSize?: number;
} = {}): Promise<ListWatchlistHitsResponse> {
  return request<ListWatchlistHitsResponse>(baseUrl(), '/v1/kyc/watchlist/queue', {
    method: 'GET',
    query: { status: query.status, page: query.page, pageSize: query.pageSize },
  });
}

export async function reviewWatchlistHit(
  id: string,
  decision: 'CONFIRMED' | 'FALSE_POSITIVE' | 'CLEARED',
  note?: string,
): Promise<WatchlistHit> {
  return request<WatchlistHit>(
    baseUrl(),
    `/v1/kyc/watchlist/hits/${encodeURIComponent(id)}/review`,
    { method: 'POST', body: { decision, ...(note !== undefined ? { note } : {}) } },
  );
}

// ─── Operator KYC Review (Slice 2 — B2) ──────────────────────────────────

export type TierUpgradeStatus =
  | 'PENDING_REVIEW'
  | 'APPROVED'
  | 'REJECTED'
  | 'AUTO_APPROVED';

export interface TierUpgradeRequest {
  readonly id: string;
  readonly citizenUserId: string;
  readonly status: TierUpgradeStatus;
  readonly currentTier: string;
  readonly targetTier: string;
  readonly nimcOutcome: string;
  readonly nimcVerifiedName: string | null;
  readonly approvedByOperatorId: string | null;
  readonly approvedAt: string | null;
  readonly rejectedByOperatorId: string | null;
  readonly rejectedAt: string | null;
  readonly rejectionReason: string | null;
  readonly createdAt: string;
  readonly updatedAt: string;
}

export interface TierUpgradeRequestList {
  readonly items: readonly TierUpgradeRequest[];
  // eslint-disable-next-line upmp/no-number-money -- row count, not money
  readonly total: number;
  readonly page: number;
  readonly pageSize: number;
}

export interface TierUpgradeStats {
   
  readonly pendingReview: number;
   
  readonly approved: number;
   
  readonly rejected: number;
   
  readonly autoApproved: number;
}

export interface AuditTrailEntry {
  readonly id: string;
  readonly citizenUserId: string;
  readonly action: 'APPROVED' | 'REJECTED';
  readonly operatorId: string;
  readonly actedAt: string;
  readonly rejectionReason: string | null;
  readonly currentTier: string;
  readonly targetTier: string;
  readonly createdAt: string;
}

export interface AuditTrailList {
  readonly items: readonly AuditTrailEntry[];
  // eslint-disable-next-line upmp/no-number-money -- row count, not money
  readonly total: number;
  readonly page: number;
  readonly pageSize: number;
}

export async function getTierUpgradeStats(): Promise<TierUpgradeStats> {
  return request<TierUpgradeStats>(baseUrl(), '/v1/kyc/tier-upgrade-requests/stats', {
    method: 'GET',
  });
}

export async function listPendingTierUpgrades(query: {
  readonly page?: number;
  readonly pageSize?: number;
} = {}): Promise<TierUpgradeRequestList> {
  return request<TierUpgradeRequestList>(baseUrl(), '/v1/kyc/tier-upgrade-requests/pending', {
    method: 'GET',
    query: { page: query.page, pageSize: query.pageSize },
  });
}

export async function getTierUpgradeRequest(id: string): Promise<TierUpgradeRequest> {
  return request<TierUpgradeRequest>(
    baseUrl(),
    `/v1/kyc/tier-upgrade-requests/${encodeURIComponent(id)}`,
    { method: 'GET' },
  );
}

export async function approveTierUpgrade(id: string): Promise<TierUpgradeRequest> {
  return request<TierUpgradeRequest>(
    baseUrl(),
    `/v1/kyc/tier-upgrade-requests/${encodeURIComponent(id)}/approve`,
    { method: 'POST', body: {} },
  );
}

export async function rejectTierUpgrade(
  id: string,
  reason: string,
): Promise<TierUpgradeRequest> {
  return request<TierUpgradeRequest>(
    baseUrl(),
    `/v1/kyc/tier-upgrade-requests/${encodeURIComponent(id)}/reject`,
    { method: 'POST', body: { reason } },
  );
}

export async function getAuditTrail(query: {
  readonly page?: number;
  readonly pageSize?: number;
} = {}): Promise<AuditTrailList> {
  return request<AuditTrailList>(baseUrl(), '/v1/kyc/audit-trail', {
    method: 'GET',
    query: { page: query.page, pageSize: query.pageSize },
  });
}

// ─── KYC Analytics summary ───────────────────────────────────────────────

export interface TierDistributionEntry {
  readonly tier: string;
   
  readonly count: number;
}

export interface AnalyticsSummary {
   
  readonly verificationRate: number;
   
  readonly avgTurnaroundSeconds: number;
   
  readonly upgradesLast30Days: number;
   
  readonly expiredVerifications: number;
   
  readonly blockedCustomers: number;
  readonly tierDistribution: readonly TierDistributionEntry[];
   
  readonly totalProfiles: number;
}

export async function getAnalyticsSummary(): Promise<AnalyticsSummary> {
  return request<AnalyticsSummary>(baseUrl(), '/v1/kyc/analytics/summary', {
    method: 'GET',
  });
}

// ─── Single watchlist hit (any status) ───────────────────────────────────

export async function getWatchlistHit(id: string): Promise<WatchlistHit> {
  return request<WatchlistHit>(
    baseUrl(),
    `/v1/kyc/watchlist/hits/${encodeURIComponent(id)}`,
    { method: 'GET' },
  );
}

// ─── Create SAR draft ────────────────────────────────────────────────────

export interface CreateSarDraftPayload {
  readonly profileId: string;
  readonly trigger: 'CASH_DEPOSIT_THRESHOLD' | 'RAPID_MOVEMENT' | 'HIGH_VALUE' | 'OTHER';
  readonly totalAmount: string;
  readonly currency?: string;
  readonly windowStart: string;
  readonly windowEnd: string;
  readonly narrative: string;
}

export async function createSarDraft(payload: CreateSarDraftPayload): Promise<SarSummary> {
  return request<SarSummary>(baseUrl(), '/v1/kyc/sars', {
    method: 'POST',
    body: payload,
  });
}

// ─── KYC profile picker (used by SAR draft form) ─────────────────────────

export interface KycProfileSummary {
  readonly id: string;
  readonly tenantId: string;
  readonly customerId: string;
  readonly currentTier: string;
  readonly riskRating: string;
  readonly amlFrozen: boolean;
  readonly createdAt: string;
}

export interface ListKycProfilesResponse {
  readonly items: readonly KycProfileSummary[];
  // eslint-disable-next-line upmp/no-number-money -- count
  readonly total: number;
  readonly page: number;
  readonly pageSize: number;
}

export async function listKycProfiles(query: {
  readonly currentTier?: string;
  readonly riskRating?: string;
  readonly amlFrozen?: boolean;
  readonly page?: number;
  readonly pageSize?: number;
} = {}): Promise<ListKycProfilesResponse> {
  return request<ListKycProfilesResponse>(baseUrl(), '/v1/kyc/profiles', {
    method: 'GET',
    query: {
      currentTier: query.currentTier,
      riskRating: query.riskRating,
      amlFrozen: query.amlFrozen,
      page: query.page,
      pageSize: query.pageSize,
    },
  });
}
